import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import Button from '../ui/Button';
import AttendanceSelector from './AttendanceSelector';
import MemberSelectionModal from './MemberSelectionModal';
import CalendarAttendanceView from './CalendarAttendanceView';

interface EnhancedAttendanceTakerProps {
    onBack: () => void;
}

type AttendanceType = 'staff' | 'teacher' | 'student';

const EnhancedAttendanceTaker: React.FC<EnhancedAttendanceTakerProps> = ({ onBack }) => {
    const [currentStep, setCurrentStep] = useState<'select-type' | 'select-member' | 'calendar'>('select-type');
    const [selectedType, setSelectedType] = useState<AttendanceType | null>(null);
    const [selectedMember, setSelectedMember] = useState<any>(null);
    const [showMemberModal, setShowMemberModal] = useState(false);

    const handleTypeSelect = (type: AttendanceType) => {
        setSelectedType(type);
        setShowMemberModal(true);
    };

    const handleMemberSelect = (member: any) => {
        setSelectedMember(member);
        setCurrentStep('calendar');
        setShowMemberModal(false);
    };

    const handleBack = () => {
        switch (currentStep) {
            case 'calendar':
                setCurrentStep('select-type');
                setSelectedMember(null);
                break;
            case 'select-member':
                setCurrentStep('select-type');
                break;
            default:
                onBack();
        }
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'select-type':
                return <AttendanceSelector onSelect={handleTypeSelect} />;
            case 'calendar':
                return (
                    <CalendarAttendanceView
                        selectedMember={selectedMember}
                        onBack={handleBack}
                    />
                );
            default:
                return <AttendanceSelector onSelect={handleTypeSelect} />;
        }
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 'select-type':
                return 'Select Attendance Type';
            case 'select-member':
                return `Select ${selectedType?.charAt(0).toUpperCase()}${selectedType?.slice(1)}`;
            case 'calendar':
                return `${selectedMember?.firstName} ${selectedMember?.lastName} - Attendance Calendar`;
            default:
                return 'Take Attendance';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center space-x-3">
                <Button
                    variant="secondary"
                    onClick={handleBack}
                    className="p-2"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">{getStepTitle()}</h2>
                    <p className="text-gray-600">Take attendance for school members</p>
                </div>
            </div>

            {/* Current Step Content */}
            {renderCurrentStep()}

            {/* Member Selection Modal */}
            {showMemberModal && selectedType && (
                <MemberSelectionModal
                    isOpen={showMemberModal}
                    onClose={() => setShowMemberModal(false)}
                    onSelect={handleMemberSelect}
                    type={selectedType}
                />
            )}
        </div>
    );
};

export default EnhancedAttendanceTaker;
